import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "@/lib/api";

type Shop = { id: number; code: string; name: string };

type DayRow = {
  date: string;
  orders: number;
  revenue: number;
  cod: number;
};

type AuditData = {
  date_from: string;
  date_to: string;
  by_pos_date: DayRow[];
  by_vtp_date: DayRow[];
  totals: {
    by_pos_total: number;
    by_vtp_total: number;
    diff: number;
  };
  diffs: {
    pos_date: string | null;
    vtp_date: string | null;
    orders: number;
    revenue: number;
  }[];
  no_vtp_data: {
    orders: number;
    revenue: number;
  };
};

const fmtVND = (v: number | null) =>
  v === null
    ? "—"
    : Number(v).toLocaleString("vi-VN", { maximumFractionDigits: 0 }) + "đ";

const today = () => new Date().toISOString().slice(0, 10);
const startOfMonth = () => today().slice(0, 8) + "01";

const fmtJustDate = (s: string | null) =>
  s ? new Date(s + (s.length === 10 ? "T00:00:00" : "")).toLocaleDateString("vi-VN") : "—";

export function AuditPage() {
  const [shopId, setShopId] = useState<number | "">("");
  const [dateFrom, setDateFrom] = useState(startOfMonth());
  const [dateTo, setDateTo] = useState(today());

  const shopsQ = useQuery({
    queryKey: ["shops"],
    queryFn: async () => (await api.get<Shop[]>("/shops")).data,
  });

  const auditQ = useQuery({
    queryKey: ["audit-revenue", shopId, dateFrom, dateTo],
    queryFn: async () => {
      const params: any = { date_from: dateFrom, date_to: dateTo };
      if (shopId) params.shop_id = shopId;
      return (await api.get<AuditData>("/audit/revenue-by-day", { params })).data;
    },
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <h1 className="text-xl font-semibold text-slate-900 mb-1">Kiểm tra doanh thu</h1>
      <p className="text-sm text-slate-500 mb-5">Đối chiếu doanh thu theo ngày để bắt sai sót.</p>
      <div className="text-sm text-gray-600 mb-4">
        So sánh doanh thu theo "ngày POScake đánh dấu Đã nhận" (cột trái) vs
        "ngày VTP phát thành công" (cột phải). Khác biệt là dấu hiệu cần investigate.
      </div>

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Cửa hàng</label>
          <select
            value={shopId}
            onChange={(e) => setShopId(e.target.value ? Number(e.target.value) : "")}
            className="border rounded px-2 py-1 text-sm min-w-[12rem]"
          >
            <option value="">— Tất cả —</option>
            {shopsQ.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} — {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Từ</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Đến</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
        </div>
      </div>

      {auditQ.isLoading && <div className="text-gray-500 text-sm">Đang tải...</div>}

      {auditQ.data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <div className="text-xs text-blue-700">Theo ngày POScake</div>
              <div className="text-2xl font-semibold">
                {fmtVND(auditQ.data.totals.by_pos_total)}
              </div>
              <div className="text-xs text-gray-500">
                {auditQ.data.by_pos_date.length} ngày có doanh thu
              </div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded p-3">
              <div className="text-xs text-green-700">Theo ngày VTP phát</div>
              <div className="text-2xl font-semibold">
                {fmtVND(auditQ.data.totals.by_vtp_total)}
              </div>
              <div className="text-xs text-gray-500">
                {auditQ.data.by_vtp_date.length} ngày có doanh thu
              </div>
            </div>
            <div
              className={`border rounded p-3 ${
                Math.abs(auditQ.data.totals.diff) < 1
                  ? "bg-gray-50 border-gray-200"
                  : "bg-yellow-50 border-yellow-200"
              }`}
            >
              <div className="text-xs text-gray-700">Chênh lệch tổng</div>
              <div
                className={`text-2xl font-semibold ${
                  Math.abs(auditQ.data.totals.diff) < 1
                    ? "text-green-700"
                    : "text-yellow-700"
                }`}
              >
                {fmtVND(auditQ.data.totals.diff)}
              </div>
              <div className="text-xs text-gray-500">
                = POS − VTP (chấp nhận ≠ 0 vì đơn ngoài khoảng / chưa giao)
              </div>
            </div>
          </div>

          {auditQ.data.no_vtp_data.orders > 0 && (
            <div className="bg-orange-50 border border-orange-200 text-orange-900 rounded p-3 mb-4 text-sm">
              <b>⚠️ {auditQ.data.no_vtp_data.orders}</b> đơn POScake "Đã nhận" trong khoảng này
              CHƯA có dữ liệu VTP (tổng {fmtVND(auditQ.data.no_vtp_data.revenue)}).
              Cần chạy lại "↻ Đồng bộ tất cả" ở trang Cửa hàng để fetch tiếp.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
            <DayTable
              title="Theo ngày POScake (received_date)"
              rows={auditQ.data.by_pos_date}
            />
            <DayTable
              title="Theo ngày VTP phát thành công (order_successdate)"
              rows={auditQ.data.by_vtp_date}
            />
          </div>

          {auditQ.data.diffs.length > 0 && (
            <div className="bg-white border rounded">
              <div className="px-3 py-2 text-sm font-medium border-b bg-gray-50">
                Đơn có POS date ≠ VTP date ({auditQ.data.diffs.length} nhóm)
              </div>
              <table className="w-full text-xs">
                <thead className="text-gray-500 bg-gray-50">
                  <tr>
                    <th className="text-left px-2 py-1">POScake date</th>
                    <th className="text-left px-2 py-1">VTP date</th>
                    <th className="text-right px-2 py-1">Số đơn</th>
                    <th className="text-right px-2 py-1">Doanh thu</th>
                    <th className="text-right px-2 py-1">Chênh ngày</th>
                  </tr>
                </thead>
                <tbody>
                  {auditQ.data.diffs.map((d, i) => {
                    let dayDiff: number | null = null;
                    if (d.pos_date && d.vtp_date) {
                      const a = new Date(d.pos_date).getTime();
                      const b = new Date(d.vtp_date).getTime();
                      dayDiff = Math.round((a - b) / (24 * 3600 * 1000));
                    }
                    return (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1">{fmtJustDate(d.pos_date)}</td>
                        <td className="px-2 py-1">{fmtJustDate(d.vtp_date)}</td>
                        <td className="px-2 py-1 text-right">{d.orders}</td>
                        <td className="px-2 py-1 text-right">{fmtVND(d.revenue)}</td>
                        <td
                          className={`px-2 py-1 text-right ${
                            dayDiff === null
                              ? "text-gray-400"
                              : Math.abs(dayDiff) >= 7
                              ? "text-red-600 font-medium"
                              : Math.abs(dayDiff) >= 2
                              ? "text-yellow-700"
                              : "text-gray-500"
                          }`}
                        >
                          {dayDiff === null ? "—" : `${dayDiff > 0 ? "+" : ""}${dayDiff} ngày`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="px-3 py-2 text-xs text-gray-500 border-t">
                Note: POScake mark "đã nhận" có thể chậm hơn VTP phát thực tế (admin đánh dấu
                thủ công sau). Sổ S2a-HKD ưu tiên dùng VTP date vì đó là ngày giao thực tế
                đúng quy định ghi sổ.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DayTable({ title, rows }: { title: string; rows: DayRow[] }) {
  const total = rows.reduce((a, r) => a + (r.revenue || 0), 0);
  return (
    <div className="bg-white border rounded">
      <div className="px-3 py-2 text-sm font-medium border-b bg-gray-50">{title}</div>
      <table className="w-full text-xs">
        <thead className="text-gray-500 bg-gray-50">
          <tr>
            <th className="text-left px-2 py-1">Ngày</th>
            <th className="text-right px-2 py-1">Đơn</th>
            <th className="text-right px-2 py-1">Doanh thu</th>
            <th className="text-right px-2 py-1">COD</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="px-2 py-4 text-center text-gray-500">
                Không có dữ liệu.
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.date} className="border-t">
              <td className="px-2 py-1">{fmtJustDate(r.date)}</td>
              <td className="px-2 py-1 text-right">{r.orders}</td>
              <td className="px-2 py-1 text-right">{fmtVND(r.revenue)}</td>
              <td className="px-2 py-1 text-right text-gray-500">{fmtVND(r.cod)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t bg-gray-50 font-medium">
            <td className="px-2 py-1">Tổng</td>
            <td className="px-2 py-1 text-right">
              {rows.reduce((a, r) => a + r.orders, 0)}
            </td>
            <td className="px-2 py-1 text-right">{fmtVND(total)}</td>
            <td className="px-2 py-1 text-right text-gray-500">
              {fmtVND(rows.reduce((a, r) => a + (r.cod || 0), 0))}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
